// default-locale.global.ts

import { useUserStore } from "~/store/user";
import { isAuthOptionalPath } from "~/utils/auth";

/**
 * Whether this request came with a console session cookie — `__Host-mw_session_id`
 * in production, `mw_session_id` in development (`server/utils/mw-session.ts`).
 * Presence only: the session is proved by `clientDetails()`, never by this.
 */
function requestCarriesSession(): boolean {
  const cookie = useRequestHeaders(['cookie']).cookie ?? '';
  return /(?:^|;\s*)(?:__Host-)?mw_session_id=/.test(cookie);
}

// Middleware to set the application's locale based on user authentication and token validity
export default defineNuxtRouteMiddleware(async (to) => {
  const userStore = useUserStore(); // Access user-related state and actions
  const localeCookie = useCookie('i18n_redirected'); // Track the active locale

  /**
   * Whether to resolve the session on this page.
   *
   * Detected by the SHELL rather than by a list of paths — the same test
   * `auth.global.ts` makes. It used to be three hardcoded paths, so every other
   * public page fired `GET /whmcs/me` for an anonymous visitor and took a 401
   * for it.
   *
   * The exception is the AUTH-OPTIONAL pages, which are chromeless but are NOT
   * anonymous-only: the public checkout serves both a first-time visitor and a
   * customer who is already signed in, and it changes substantially between the
   * two — the header greets them, the identity step disappears, their
   * subscriptions and images load. Skipping resolution there left a signed-in
   * customer looking permanently anonymous to the page: still asked to "enter
   * your billing details", still offered a Login button. The session cookie is
   * httpOnly, so there is no way to detect one without asking; one request that
   * may 401 is the price, and it is paid only on those routes.
   */
  const layout = to.meta.layout
  const isChromeless = layout === 'auth' || layout === false
  const resolveSession = !isChromeless || isAuthOptionalPath(to.path)

  /**
   * On the SERVER as well, for an auth-optional page whose request carries a
   * session cookie.
   *
   * httpOnly hides that cookie from the browser, not from the request. Without
   * this, `/configure` served to a signed-in customer rendered in the BROWSER's
   * language and as an anonymous visitor, then hydrated into the profile's
   * language and the signed-in page: every text node mismatched ("Hydration
   * completed but contains mismatches") and the customer watched English turn
   * into Portuguese. A request with no cookie still costs no call, so a
   * first-time visitor is exactly as before. Protected pages need none of this:
   * `auth.global.ts` has already resolved them on the server.
   */
  const resolveOnServer =
    import.meta.server && isChromeless && isAuthOptionalPath(to.path) && requestCarriesSession()

  if (resolveOnServer && !userStore.user && userStore.sessionStatus === 'unknown') {
    try {
      await userStore.clientDetails();
      userStore.setSessionStatus('authenticated');
    } catch (error) {
      // Only a definitive rejection means "signed out"; anything else leaves
      // the status unknown, and the browser asks again below.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) userStore.setSessionStatus('unauthenticated');
    }
  }

  if (import.meta.client && resolveSession && userStore.sessionStatus !== 'unauthenticated') {
    try {
      if (!userStore.user) {
        await userStore.clientDetails(); // Fetch user details from the server
      }
      userStore.setSessionStatus('authenticated');
    } catch (error) {
      userStore.setSessionStatus('unauthenticated');
    }
  }

  const preferredLocale = userStore.getUserPreferredLocale?.();

  if (preferredLocale && localeCookie.value !== preferredLocale) {
    await userStore.setUserDefaultLanguage();
    localeCookie.value = preferredLocale;
    return;
  }

  if (!localeCookie.value) {
    await userStore.setBrowserDefaultLanguage();
  }
});
